'use client';

import { useEffect, useState, useRef, useCallback } from 'react';

interface FrameRateData {
  fps: number;
  normalized: number;
  history: number[];
  isStable: boolean;
  simulateLoad: () => void;
}

let globalStressLevel = 0;

export function stressTest(duration = 2000) {
  globalStressLevel = 1;
  setTimeout(() => {
    globalStressLevel = 0;
  }, duration);
}

export function useFrameRate(historyLength = 60): FrameRateData {
  const frameTimesRef = useRef<number[]>([]);
  const lastTimeRef = useRef(0);
  const historyRef = useRef<number[]>([]);
  const rafRef = useRef<number | undefined>(undefined);

  const simulateLoad = useCallback(() => {
    stressTest(3000);
  }, []);

  const [data, setData] = useState<FrameRateData>({
    fps: 60,
    normalized: 1,
    history: [],
    isStable: true,
    simulateLoad,
  });

  useEffect(() => {
    if (lastTimeRef.current === 0) {
      lastTimeRef.current = performance.now();
    }

    const measureFrame = () => {
      const now = performance.now();
      const delta = now - lastTimeRef.current;
      lastTimeRef.current = now;

      if (globalStressLevel > 0) {
        const blockTime = Math.random() * 50 * globalStressLevel;
        const start = performance.now();
        while (performance.now() - start < blockTime) {}
      }

      frameTimesRef.current.push(delta);
      if (frameTimesRef.current.length > 30) {
        frameTimesRef.current.shift();
      }

      const avgDelta =
        frameTimesRef.current.reduce((a, b) => a + b, 0) /
        frameTimesRef.current.length;
      let fps = Math.min(1000 / avgDelta, 120); // Cap at 120

      fps = fps * (0.9 + Math.random() * 0.2);

      let normalized = Math.min(fps / 120, 1);

      if (globalStressLevel > 0) {
        normalized *= 1 - globalStressLevel * 0.5 * Math.random();
      }

      historyRef.current.push(normalized);
      if (historyRef.current.length > historyLength) {
        historyRef.current.shift();
      }

      const recentFrames = frameTimesRef.current.slice(-10);
      const avgRecent =
        recentFrames.reduce((a, b) => a + b, 0) / recentFrames.length;
      const variance =
        recentFrames.reduce((sum, t) => sum + Math.pow(t - avgRecent, 2), 0) /
        recentFrames.length;
      const isStable = variance < 50 && globalStressLevel === 0;

      setData({
        fps: Math.round(fps),
        normalized,
        history: [...historyRef.current],
        isStable,
        simulateLoad,
      });

      rafRef.current = requestAnimationFrame(measureFrame);
    };

    rafRef.current = requestAnimationFrame(measureFrame);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [historyLength, simulateLoad]);

  return data;
}
